import React, { useRef } from 'react';

import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';

import { TreeNode } from '../../types/interfaces';

import { EntityVisibility } from './EntityVisibility';
import VisibilityControlToggle from './VisibilityControlToggle';

export interface CalendarAssignmentsMobileActionsProps {
  selectedDepartmentFilter: TreeNode | null;
  departmentIsSelected: boolean;
  addMenuItems: MenuItem[];
  deleteMenuItems: MenuItem[];
  hasUnsavedChanges: boolean;
  onSaveChanges: () => void;
  onRecalculateClick: () => void;
  visibleEntities: EntityVisibility;
  handleApplyVisibility: (entities: EntityVisibility) => void;
  viewMode: 'schedule' | 'timesheet';
  onToggleViewMode: () => void;
  onToggleExportDialogVisible: () => void;
  onHistoryClick: () => void;
  exportTemplate: () => void;
  importAbsencesXLS: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasHorizontalScroll: boolean;
  unassignedTasksCount: number;
  onOpenUnassignedTasks: () => void;
  isMobile: boolean;
}

export interface CalendarAssignmentsMobileActionsResult {
  buttonsLeft: React.ReactNode[];
  buttonsRight: React.ReactNode[];
}

export function useCalendarAssignmentsMobileActions({
  selectedDepartmentFilter,
  departmentIsSelected,
  addMenuItems,
  deleteMenuItems,
  hasUnsavedChanges,
  onSaveChanges,
  onRecalculateClick,
  visibleEntities,
  handleApplyVisibility,
  viewMode,
  onToggleViewMode,
  onToggleExportDialogVisible,
  onHistoryClick,
  exportTemplate,
  importAbsencesXLS,
  hasHorizontalScroll,
  unassignedTasksCount,
  onOpenUnassignedTasks,
  isMobile
}: CalendarAssignmentsMobileActionsProps): CalendarAssignmentsMobileActionsResult {
  const addMenuRef = useRef<Menu>(null);
  const deleteMenuRef = useRef<Menu>(null);
  const moreMenuRef = useRef<Menu>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const moreMenuItems = [
    {
      label: 'Рассчитать график',
      icon: 'pi pi-calculator',
      command: onRecalculateClick,
      disabled: !selectedDepartmentFilter || viewMode === 'timesheet'
    },
    {
      label: viewMode === 'schedule' ? 'Просмотр табеля' : 'Просмотр графика',
      icon: viewMode === 'schedule' ? 'pi pi-table' : 'pi pi-calendar',
      command: onToggleViewMode
    },
    {
      label: viewMode === 'schedule' ? 'Экспорт графика' : 'Экспорт табеля',
      icon: 'pi pi-download',
      command: onToggleExportDialogVisible
    },
    viewMode === 'schedule'
      ? {
          label: 'Выгрузка шаблона импорта',
          icon: 'pi pi-file-excel',
          command: exportTemplate
        }
      : null,
    viewMode === 'schedule'
      ? {
          label: 'Импорт',
          icon: 'pi pi-file-import',
          command: () => {
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            fileInputRef.current?.click();
          }
        }
      : null,
    {
      label: 'История изменений',
      icon: 'pi pi-history',
      command: onHistoryClick
    }
  ].filter(Boolean) as MenuItem[];

  const buttonsLeft = [
    departmentIsSelected && !isMobile && (
      <Button
        key="unassigned-tasks-mobile"
        icon="pi pi-list"
        severity="secondary"
        className="calendarAssignments-unassigned-btn calendarAssignments-unassigned-mobile-btn calendarAssignments-mobile-action-btn p-button-rounded p-button-outlined"
        tooltip="Неназначенные"
        tooltipOptions={{ position: 'top' }}
        onClick={onOpenUnassignedTasks}
        badge={String(unassignedTasksCount ?? 0)}
        badgeClassName={
          (unassignedTasksCount ?? 0) > 0
            ? 'unassignedTasks-badge unassignedTasks-badge--danger'
            : 'unassignedTasks-badge unassignedTasks-badge--success'
        }
      />
    ),
    <VisibilityControlToggle
      key="visibility-toggle-mobile"
      visibleEntities={visibleEntities}
      onApply={handleApplyVisibility}
    />
  ].filter(Boolean) as React.ReactNode[];

  const buttonsRight = [
    viewMode === 'schedule' && (
      <input
        key="input_file_mobile"
        type="file"
        accept="csv"
        onChange={importAbsencesXLS}
        style={{ visibility: 'hidden', width: '0px' }}
        ref={fileInputRef}
      />
    ),
    hasUnsavedChanges && (
      <Button
        key="save-changes-mobile"
        icon="pi pi-save"
        className="calendarAssignments-save-btn__roles calendarAssignments-mobile-action-btn p-button-rounded p-button-success"
        onClick={onSaveChanges}
        rounded
        tooltip="Сохранить изменения"
        tooltipOptions={{ position: 'top' }}
      />
    ),
    <Button
      key="calendarAssignments-more-mobile"
      className="calendarAssignments-more-btn__roles calendarAssignments-mobile-action-btn p-button-rounded p-button-secondary"
      type="button"
      icon="pi pi-ellipsis-h"
      tooltip="Ещё"
      tooltipOptions={{ position: 'top' }}
      onClick={(e) => moreMenuRef.current?.toggle(e)}
    />,
    <Menu
      key="calendarAssignments-more-menu-mobile"
      model={moreMenuItems}
      popup
      ref={moreMenuRef}
    />,
    viewMode === 'schedule' && departmentIsSelected && (
      <React.Fragment key="add-menu-mobile">
        <Button
          key="add-shift-mobile"
          className="calendarAssignments-add-btn__roles calendarAssignments-mobile-action-btn"
          type="button"
          icon="pi pi-plus"
          severity="success"
          rounded
          tooltip="Добавить"
          tooltipOptions={{ position: 'top' }}
          onClick={(e) => addMenuRef.current?.toggle(e)}
        />
        <Menu model={addMenuItems} popup ref={addMenuRef} />
      </React.Fragment>
    ),
    viewMode === 'schedule' && departmentIsSelected && (
      <React.Fragment key="delete-menu-mobile">
        <Button
          key={`delete-btn-mobile-${hasHorizontalScroll}`}
          icon="pi pi-trash"
          className="calendarAssignments-massDelete-btn__roles calendarAssignments-mobile-action-btn p-button-rounded p-button-danger"
          tooltip="Удаление"
          onClick={(e) => deleteMenuRef.current?.toggle(e)}
          disabled={!selectedDepartmentFilter}
          tooltipOptions={{ position: 'top' }}
        />
        <Menu model={deleteMenuItems} popup ref={deleteMenuRef} />
      </React.Fragment>
    )
  ].filter(Boolean) as React.ReactNode[];

  return { buttonsLeft, buttonsRight };
}
